import { useCallback, useEffect, useRef, useState } from 'react';

interface UseAutoRefreshOptions {
  refreshFn: () => Promise<void> | void;
  enabled?: boolean;
  intervalMs?: number;
  pauseWhenHidden?: boolean;
}

interface UseAutoRefreshResult {
  isRefreshing: boolean;
  lastRefreshedAt: number | null;
  secondsSinceRefresh: number | null;
  refreshNow: () => Promise<void>;
}

const DEFAULT_INTERVAL_MS = 30000;

export default function useAutoRefresh({
  refreshFn,
  enabled = true,
  intervalMs = DEFAULT_INTERVAL_MS,
  pauseWhenHidden = true,
}: UseAutoRefreshOptions): UseAutoRefreshResult {
  const refreshRef = useRef(refreshFn);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    refreshRef.current = refreshFn;
  }, [refreshFn]);

  const runRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refreshRef.current();
      setLastRefreshedAt(Date.now());
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled || intervalMs <= 0) return;

    const canRun = () => !(pauseWhenHidden && typeof document !== 'undefined' && document.hidden);

    const tick = async () => {
      if (!canRun()) return;
      await runRefresh();
    };

    const timer = window.setInterval(() => {
      void tick();
    }, intervalMs);

    const onVisibilityChange = () => {
      if (!canRun()) return;
      void runRefresh();
    };

    if (pauseWhenHidden) {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }

    return () => {
      window.clearInterval(timer);
      if (pauseWhenHidden) {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
    };
  }, [enabled, intervalMs, pauseWhenHidden, runRefresh]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const secondsSinceRefresh =
    lastRefreshedAt == null ? null : Math.max(0, Math.floor((now - lastRefreshedAt) / 1000));

  return {
    isRefreshing,
    lastRefreshedAt,
    secondsSinceRefresh,
    refreshNow: runRefresh,
  };
}
